import React from 'react';
import {
  BriefcaseBusiness,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Link2,
  LucideIcon,
  SearchX,
  UserRound,
} from 'lucide-react';
import { SearchResult, SearchResultItem, SearchEntityType, SearchHighlight } from '../../types/search';
import { searchService } from '../../services/searchService';
import { Card, EmptyState, ENTITY_TONE, EntityIcon, entityKindOf, IconButton, Skeleton } from '../ui';

interface SearchResultsProps {
  results: SearchResult;
  onPageChange: (page: number) => void;
  onResultClick: (item: SearchResultItem) => void;
  isLoading?: boolean;
}

const entityIcons: Record<string, LucideIcon> = {
  [SearchEntityType.STAKEHOLDER]: UserRound,
  [SearchEntityType.ORGANIZATION]: Building2,
  [SearchEntityType.ASSIGNMENT]: BriefcaseBusiness,
  [SearchEntityType.WIKI_PAGE]: FileText,
  [SearchEntityType.RELATIONSHIP]: Link2,
  [SearchEntityType.MEDIA_ASSET]: ImageIcon,
};

/** Fields already shown as the title; their highlights only feed the term list */
const TITLE_FIELDS = new Set(['firstName', 'lastName', 'name', 'title', 'originalName', 'fileName']);

const MARK_CLASS = 'rounded-[3px] bg-neutral-200/70 px-px text-ink';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Matched terms, taken from the <mark> tags the server returns */
function matchedTerms(highlights: SearchHighlight[] = []): string[] {
  const terms = new Set<string>();
  highlights.forEach((highlight) =>
    highlight.fragments?.forEach((fragment) => {
      const re = /<mark>(.*?)<\/mark>/gi;
      let match: RegExpExecArray | null;
      while ((match = re.exec(fragment))) {
        if (match[1].trim()) terms.add(match[1].trim().toLowerCase());
      }
    }),
  );
  return Array.from(terms);
}

function highlightText(text: string, terms: string[]): React.ReactNode {
  if (!text || terms.length === 0) return text;
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  return text.split(pattern).map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className={MARK_CLASS}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

/** First highlight outside the title, as escaped text plus <mark> */
function snippetHtml(highlights: SearchHighlight[] = []): string | null {
  const highlight = highlights.find((h) => !TITLE_FIELDS.has(h.field) && h.fragments?.length);
  if (!highlight) return null;
  // Escape everything, then restore only the server's <mark> tags
  return highlight.fragments[0]
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/&lt;(\/?)mark&gt;/g, '<$1mark>');
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  onPageChange,
  onResultClick,
  isLoading = false,
}) => {
  const renderResultItem = (item: SearchResultItem) => {
    const title = searchService.formatResultTitle(item.data, item.entityType);
    const subtitle = searchService.formatResultSubtitle(item.data, item.entityType);
    const Icon = entityIcons[item.entityType] || FileText;
    const kind = entityKindOf(item.entityType);
    const typeName = searchService.getEntityTypeDisplayName(item.entityType);
    const terms = matchedTerms(item.highlights);
    const snippet = snippetHtml(item.highlights);

    return (
      <li key={`${item.entityType}-${item.id}`}>
        <button
          type="button"
          onClick={() => onResultClick(item)}
          className="group flex min-h-[56px] w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none active:bg-neutral-100/70 sm:min-h-[52px]"
        >
          {kind ? (
            <EntityIcon kind={kind} icon={Icon} size="md" title={typeName} />
          ) : (
            <span
              className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-neutral-100 text-neutral-500"
              title={typeName}
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
          )}

          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] leading-snug text-ink">{highlightText(title, terms)}</span>
            {(subtitle || snippet) && (
              <span className="mt-0.5 block truncate text-[12px] leading-snug text-neutral-500">
                {subtitle && highlightText(subtitle, terms)}
                {subtitle && snippet && <span className="mx-1 text-neutral-300">·</span>}
                {snippet && (
                  <span
                    className="[&_mark]:rounded-[3px] [&_mark]:bg-neutral-200/70 [&_mark]:px-px [&_mark]:text-ink"
                    dangerouslySetInnerHTML={{ __html: snippet }}
                  />
                )}
              </span>
            )}
          </span>

          <span
            className={`hidden h-5 flex-shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none sm:inline-flex ${
              kind ? ENTITY_TONE[kind].soft : 'bg-neutral-100 text-neutral-600'
            }`}
          >
            {typeName}
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300 sm:hidden" strokeWidth={1.75} />
        </button>
      </li>
    );
  };

  const renderPagination = () => {
    if (results.totalPages <= 1) return null;

    const { page, totalPages } = results;
    const pages: number[] = [];

    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const pageButton = (pageNum: number) => (
      <button
        key={pageNum}
        type="button"
        onClick={() => onPageChange(pageNum)}
        aria-current={pageNum === page ? 'page' : undefined}
        className={`inline-flex h-9 min-w-[36px] items-center justify-center rounded-full px-2 text-[13px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-8 sm:min-w-[32px] ${
          pageNum === page ? 'bg-ink font-medium text-white' : 'text-neutral-500 hover:bg-neutral-100 hover:text-ink'
        }`}
      >
        {pageNum}
      </button>
    );

    return (
      <nav className="mt-4 flex items-center justify-between gap-1 sm:justify-center" aria-label="Pagination">
        <IconButton label="Previous page" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>

        {/* Phones: where you are — the numbered row is wider than a small screen */}
        <span className="text-[13px] tabular-nums text-neutral-500 sm:hidden">
          Page {page} of {totalPages}
        </span>

        <span className="hidden sm:contents">
          {startPage > 1 && (
            <>
              {pageButton(1)}
              {startPage > 2 && <span className="px-1 text-[13px] text-neutral-300">…</span>}
            </>
          )}

          {pages.map(pageButton)}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-1 text-[13px] text-neutral-300">…</span>}
              {pageButton(totalPages)}
            </>
          )}
        </span>

        <IconButton label="Next page" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </nav>
    );
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading results">
        <Skeleton className="mb-2.5 h-3 w-28" />
        <Card divided>
          {[...Array(6)].map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-8 w-8 flex-shrink-0 rounded-[10px]" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-2/5" />
                <Skeleton className="h-3 w-3/5" />
              </div>
            </div>
          ))}
        </Card>
      </div>
    );
  }

  // No results
  if (!results.items || results.items.length === 0) {
    return (
      <Card>
        <EmptyState icon={SearchX} title="No results found" className="py-14" />
      </Card>
    );
  }

  return (
    <div>
      <p className="mb-2 flex h-5 items-center text-[12px] tabular-nums text-neutral-400">
        {results.total.toLocaleString()} {results.total === 1 ? 'result' : 'results'}
        <span className="mx-1.5 text-neutral-300">·</span>
        {results.executionTime}ms
      </p>

      <Card clip>
        <ul className="divide-y divide-neutral-200/70">{results.items.map(renderResultItem)}</ul>
      </Card>

      {renderPagination()}
    </div>
  );
};
